import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import type { SpecimenCatalog, SpecimenCatalogItem } from "../api/types";
import { catalogDisplayName, intraopCopy } from "../lib/intraopCopy";

type SortDir = "asc" | "desc";

export function SpecimenCatalogSettings() {
  const [lists, setLists] = useState<SpecimenCatalog[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [newListName, setNewListName] = useState("");
  const [addingList, setAddingList] = useState(false);
  const [query, setQuery] = useState("");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [editingList, setEditingList] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draftName, setDraftName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const data = await api<SpecimenCatalog[]>("/api/specimen-lists");
    setLists(data);
    setError("");
    setSelectedId((current) => {
      if (current && data.some((row) => row.id === current)) return current;
      return data.find((row) => row.is_default)?.id ?? data[0]?.id ?? null;
    });
  }, []);

  useEffect(() => {
    load().catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [load]);

  useEffect(() => {
    setEditingList(false);
    setAddingList(false);
    setNewListName("");
    setEditingId(null);
    setDraftName("");
    setQuery("");
  }, [selectedId]);

  const selected = useMemo(
    () => lists.find((row) => row.id === selectedId) ?? null,
    [lists, selectedId],
  );

  const selectedTitle = selected
    ? catalogDisplayName(selected.name, selected.is_default)
    : intraopCopy.listLabel;

  const rows = useMemo(() => {
    if (!selected) return [];
    const needle = query.trim().toLowerCase();
    const filtered = needle
      ? selected.items.filter(
          (item) => item.id === editingId || item.name.toLowerCase().includes(needle),
        )
      : selected.items.slice();
    filtered.sort((a, b) => {
      const cmp = a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
      return sortDir === "asc" ? cmp : -cmp;
    });
    if (editingId != null) {
      const idx = filtered.findIndex((item) => item.id === editingId);
      if (idx > 0) {
        const [row] = filtered.splice(idx, 1);
        filtered.unshift(row);
      }
    }
    return filtered;
  }, [selected, query, sortDir, editingId]);

  async function run(work: () => Promise<void>) {
    setBusy(true);
    setError("");
    try {
      await work();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function addList() {
    const name = newListName.trim();
    if (!name) {
      setAddingList(true);
      return;
    }
    await run(async () => {
      const created = await api<SpecimenCatalog>("/api/specimen-lists", {
        method: "POST",
        body: JSON.stringify({ name }),
      });
      setNewListName("");
      setAddingList(false);
      setSelectedId(created.id);
    });
  }

  async function renameList(name: string) {
    if (!selected) return;
    const next = name.trim();
    if (!next || next === selected.name) return;
    await run(async () => {
      await api(`/api/specimen-lists/${selected.id}`, {
        method: "PATCH",
        body: JSON.stringify({ name: next }),
      });
    });
  }

  async function deleteList() {
    if (!selected || selected.is_default) return;
    await run(async () => {
      await api(`/api/specimen-lists/${selected.id}`, { method: "DELETE" });
    });
  }

  async function flushEdit() {
    if (editingId == null || !selected) return;
    const current = selected.items.find((item) => item.id === editingId);
    if (current) await saveEdit(current);
  }

  async function addItem() {
    if (!selected) return;
    await flushEdit();
    setQuery("");
    await run(async () => {
      const created = await api<SpecimenCatalogItem>(`/api/specimen-lists/${selected.id}/items`, {
        method: "POST",
        body: JSON.stringify({ name: "" }),
      });
      setEditingId(created.id);
      setDraftName("");
    });
  }

  function startEdit(item: SpecimenCatalogItem) {
    if (editingId === item.id) return;
    void (async () => {
      await flushEdit();
      setEditingId(item.id);
      setDraftName(item.name);
    })();
  }

  function cancelEdit() {
    setEditingId(null);
    setDraftName("");
  }

  async function saveEdit(item: SpecimenCatalogItem) {
    if (!selected || editingId !== item.id) return;
    const next = draftName.trim();
    cancelEdit();
    if (next === item.name) return;
    await run(async () => {
      await api(`/api/specimen-lists/${selected.id}/items/${item.id}`, {
        method: "PATCH",
        body: JSON.stringify({ name: next }),
      });
    });
  }

  async function deleteItem(itemId: number) {
    if (!selected) return;
    if (editingId === itemId) cancelEdit();
    else await flushEdit();
    await run(async () => {
      await api(`/api/specimen-lists/${selected.id}/items/${itemId}`, { method: "DELETE" });
    });
  }

  function toggleSort() {
    setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
  }

  return (
    <section className="specimen-catalog">
      <div className="toolbar">
        <div>
          <strong>{intraopCopy.specimenSectionTitle}</strong>
          {selected && <span className="muted"> · {selectedTitle}</span>}
        </div>
        <div className="toolbar-actions">
          {editingList && selected ? (
            <input
              autoFocus
              defaultValue={selected.name}
              disabled={busy}
              onBlur={(e) => {
                void renameList(e.target.value);
                setEditingList(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
                if (e.key === "Escape") {
                  e.preventDefault();
                  setEditingList(false);
                }
              }}
              aria-label={intraopCopy.listLabel}
            />
          ) : (
            <select
              value={selectedId ?? ""}
              disabled={busy || !lists.length}
              onChange={(e) => {
                const nextId = Number(e.target.value);
                void (async () => {
                  await flushEdit();
                  setSelectedId(nextId);
                })();
              }}
              aria-label={intraopCopy.listLabel}
            >
              {lists.map((list) => (
                <option key={list.id} value={list.id}>
                  {catalogDisplayName(list.name, list.is_default)}
                </option>
              ))}
            </select>
          )}
          {selected && (
            <span className="people-actions">
              <button type="button" disabled={busy || editingList} onClick={() => setEditingList(true)}>
                Edit
              </button>
              {!selected.is_default && (
                <button type="button" className="danger" disabled={busy} onClick={() => void deleteList()}>
                  Delete
                </button>
              )}
            </span>
          )}
          <input
            type="search"
            placeholder="Search"
            value={query}
            disabled={busy}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search"
          />
          {addingList && (
            <input
              className="specimen-add-list"
              autoFocus
              value={newListName}
              disabled={busy}
              onChange={(e) => setNewListName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void addList();
                if (e.key === "Escape") {
                  e.preventDefault();
                  setAddingList(false);
                  setNewListName("");
                }
              }}
              aria-label={intraopCopy.listLabel}
            />
          )}
          <button
            type="button"
            className="tool-btn"
            disabled={busy || (addingList && !newListName.trim())}
            onClick={() => void addList()}
          >
            {intraopCopy.addList}
          </button>
          {selected && (
            <button type="button" className="tool-btn" disabled={busy} onClick={() => void addItem()}>
              {intraopCopy.addSpecimenToList(selectedTitle)}
            </button>
          )}
        </div>
      </div>
      {error && <div className="banner-error">{error}</div>}

      {selected && (
        <div className="people-table-wrap specimen-table-wrap">
          <table className="people-table records-table specimen-table">
            <thead>
              <tr>
                <th aria-sort={sortDir === "asc" ? "ascending" : "descending"}>
                  <button type="button" onClick={toggleSort}>
                    {intraopCopy.specimenSectionTitle}
                  </button>
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((item) => {
                const editing = editingId === item.id;
                return (
                  <tr key={item.id}>
                    <td>
                      {editing ? (
                        <input
                          autoFocus
                          value={draftName}
                          disabled={busy}
                          onChange={(e) => setDraftName(e.target.value)}
                          onBlur={() => void saveEdit(item)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") e.currentTarget.blur();
                            if (e.key === "Escape") {
                              e.preventDefault();
                              cancelEdit();
                            }
                          }}
                        />
                      ) : (
                        <button type="button" className="specimen-name" onClick={() => startEdit(item)}>
                          {item.name || "—"}
                        </button>
                      )}
                    </td>
                    <td className="people-actions">
                      <button
                        type="button"
                        disabled={busy || editing}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => startEdit(item)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="danger"
                        disabled={busy}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => void deleteItem(item.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!rows.length && (
                <tr>
                  <td colSpan={2}>—</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
